/**
 * Session bootstrap: auth, runtime parameters, profile, position watching,
 * push registration. Called once from App.
 */
import { withDefaults } from '@drawearth/shared';
import { api, ApiError } from './api.ts';
import { auth } from './auth.ts';
import { drawingAvailable, registerPush, watchPosition } from './native.ts';
import { appStore, toast } from './store.ts';

let stopWatch: (() => void) | null = null;
let started = false;

export async function refreshUser(): Promise<void> {
  if (!auth.isSignedIn()) {
    appStore.set({ user: null, signedIn: false });
    return;
  }
  try {
    const me = await api.me();
    appStore.set({ user: me, signedIn: true });
  } catch (err) {
    if (err instanceof ApiError && err.code === 'not_registered') appStore.set({ user: null, signedIn: true });
    else if (err instanceof ApiError && err.status === 401) appStore.set({ user: null, signedIn: false });
    else appStore.set({ signedIn: true });
  }
}

export async function refreshUnread(): Promise<void> {
  if (!appStore.get().user) return;
  try {
    const { notifications } = await api.notifications();
    appStore.set({ unread: notifications.filter((n) => n.readAt === null).length });
  } catch {
    // ignore
  }
}

export async function startPositionWatch(): Promise<void> {
  if (stopWatch || !drawingAvailable()) return;
  stopWatch = await watchPosition(
    (p) => appStore.set({ position: p, positionError: null }),
    (msg) => appStore.set({ positionError: msg }),
  );
}

export function stopPositionWatch(): void {
  stopWatch?.();
  stopWatch = null;
}

export async function startSession(): Promise<void> {
  if (started) return;
  started = true;
  api.setTokenProvider(() => auth.getToken());
  try {
    await auth.init();
  } catch (err) {
    console.error('auth init failed', err);
  }
  auth.onChange(() => {
    void refreshUser().then(refreshUnread);
  });
  const [params] = await Promise.all([api.parameters().catch(() => null), refreshUser()]);
  if (params) appStore.set({ params: withDefaults(params.parameters) });
  appStore.set({ authReady: true });
  void refreshUnread();
  void registerPush(
    async (platform, token) => {
      if (appStore.get().user) await api.addPushToken(platform, token).catch(() => undefined);
    },
    (link) => {
      if (link) location.assign(link);
    },
  );
}

/** Sign in with a provider, then ensure the account exists (age gate handled by the caller). */
export async function completeSignUp(): Promise<void> {
  try {
    const me = await api.signUp();
    appStore.set({ user: me, signedIn: true });
  } catch (err) {
    if (err instanceof ApiError) toast(`Sign-up failed: ${err.message}`);
    throw err;
  }
}

export async function signOut(): Promise<void> {
  stopPositionWatch();
  await auth.signOut();
  appStore.set({ user: null, signedIn: false, unread: 0, position: null });
}

export async function deleteAccount(): Promise<void> {
  await api.deleteAccount();
  try {
    await auth.deleteAuthAccount();
  } catch {
    // The Firebase account may need a recent sign-in; the API record is already gone.
  }
  await signOut();
}
